import { useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";

import type { FileTreeType } from "./groupByFolder";
import { IsInPortrait } from "../../state/selectors";
import Level from "./Level";
import { OwnerName } from "../../state/authSelectors";
import { SortStoriesBy } from "../../state/uiAtoms";
import { StyledFile } from "./File";
import { focusVisible, measures, text } from "@quickpoint.me/shared";
import styled from "styled-components";

const StyledSearchInputContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 1; // Ensure it stays above scrolling content
  background-color: ${(props) => props.theme.background}; // Prevent content showing through
  padding: 5px 10px;
  border-bottom: 1px solid ${(props) => props.theme.navigationFrame};
`;

const StyledSearchInput = styled.input`
  width: 100%;
  padding: 8px;
  box-sizing: border-box; // Include padding in the element's total width and height
  border: 1px solid ${(props) => props.theme.navigationFrame};
  background-color: ${(props) => props.theme.inputBackground};
  color: ${(props) => props.theme.text};
  border-radius: 4px;

  ${focusVisible}
  &:focus {
    border-color: ${(props) => props.theme.focus};
  }
`;

const StyledFileTree = styled.div<{ $isInPortrait: boolean }>`
  border: 1px solid ${(props) => props.theme.navigationFrame};
  display: flex; // Use flexbox for layout
  flex-direction: column; // Stack children vertically
  margin: ${(props) =>
		props.$isInPortrait
			? measures.dialogPanel.portrait.margin
			: measures.dialogPanel.landscape.margin};
  font-family: ${(props) => props.theme.font};
  overflow-y: scroll;
  overflow-x: hidden;
  // create a css style that removes all styles from buttons
  button {
    all: unset;
    cursor: pointer;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 10px;
    &:hover {
      background-color: ${(props) => props.theme.hover};
    }
    &:active {
      background-color: ${(props) => props.theme.hover};
    }
    ${focusVisible}
  }
`;

// Helper to check if a value is a file node (StackCfKey)
function isFileNodeValue(value: unknown): value is { isFile: true } {
	return (
		typeof value === "object" &&
		value !== null &&
		"isFile" in value &&
		value.isFile === true
	);
}

function filterFileTree(node: FileTreeType, term: string): FileTreeType {
	const lower = term.toLowerCase();
	const flat: FileTreeType = {};
	if (!term) return node;

	const walk = (current: FileTreeType, path: string) => {
		for (const key in current) {
			if (key === "isFolder") continue;
			const value = current[key];
			const newPath = path ? `${path}/${key}` : key;

			if (isFileNodeValue(value)) {
				if (!term || key.toLowerCase().includes(lower)) {
					flat[newPath] = value;
				}
			} else if (typeof value === "object" && value !== null) {
				walk(value as FileTreeType, newPath);
			}
		}
	};

	walk(node, "");
	return flat;
}

function FileTree({
	fileTree,
	showStacks,
}: {
	fileTree: FileTreeType;
	showStacks: boolean;
}) {
	const isInPortrait = useRecoilValue(IsInPortrait);
	const [sortStoriesBy, setSortStoriesBy] = useRecoilState(SortStoriesBy);
	const [searchTerm, setSearchTerm] = useState("");
	const ownerName = useRecoilValue(OwnerName);
	const prevShowStacksRef = useRef(showStacks);

	useEffect(() => {
		if (!prevShowStacksRef.current && showStacks) {
			setSearchTerm("");
		}
		prevShowStacksRef.current = showStacks;
	}, [showStacks]);

	if (!ownerName) return;
	const ownerNode =
		typeof fileTree[ownerName] === "object" && fileTree[ownerName] !== null
			? (fileTree[ownerName] as FileTreeType)
			: {};

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};

	let treeForLevel: FileTreeType;
	if (searchTerm) {
		const flatMap = filterFileTree(ownerNode, searchTerm);
		treeForLevel = {
			[ownerName]: { ...flatMap, isFolder: true },
		};
	} else {
		treeForLevel = fileTree;
	}

	// Common content for both portrait and landscape
	const searchInput = (
		<StyledSearchInputContainer>
			<StyledSearchInput
				type="text"
				placeholder={text.stacks.searchStories}
				value={searchTerm}
				onChange={handleSearchChange}
			/>
		</StyledSearchInputContainer>
	);

	const treeContent = <Level fileTree={treeForLevel} />;

	if (isInPortrait) {
		return (
			<StyledFileTree $isInPortrait={isInPortrait} className="styled-file-tree">
				{searchInput}
				{treeContent}
			</StyledFileTree>
		);
	}

	// Landscape view
	return (
		<StyledFileTree $isInPortrait={isInPortrait} className="styled-file-tree">
			{searchInput}
			{/* Header Row */}
			<StyledFile
				$isInLandscape={!isInPortrait}
				className="files-header"
				$noRollOver={true}
			>
				<div className="header-edit">
					<span>{text.stacks.edit}</span>
				</div>
				<div className="header-delete">
					<span>{text.stacks.delete}</span>
				</div>

				<div>
					<button
						className="header-filename"
						onClick={() => setSortStoriesBy("stackCfKey")}
						onKeyDown={(e) =>
							e.key === "Enter" && setSortStoriesBy("stackCfKey")
						}
						tabIndex={0}
						type="button"
					>
						<span>{text.stacks.stories}</span>
						{sortStoriesBy === "stackCfKey" && (
							<span className="sort-indicator">▼</span>
						)}
					</button>
				</div>
				<div>
					<button
						className="header-updated"
						onClick={() => setSortStoriesBy("updated")}
						onKeyDown={(e) => e.key === "Enter" && setSortStoriesBy("updated")}
						tabIndex={0}
						type="button"
					>
						<span>{text.stacks.updated}</span>
						{sortStoriesBy === "updated" && (
							<span className="sort-indicator">▼</span>
						)}
					</button>
				</div>
				<div>
					<button
						className="header-created"
						onClick={() => setSortStoriesBy("created")}
						onKeyDown={(e) => e.key === "Enter" && setSortStoriesBy("created")}
						tabIndex={0}
						type="button"
					>
						<span>{text.stacks.created}</span>
						{sortStoriesBy === "created" && (
							<span className="sort-indicator">▼</span>
						)}
					</button>
				</div>
			</StyledFile>
			{treeContent}
		</StyledFileTree>
	);
}

export default FileTree;
